import { type ReactNode } from "react";
import { Loader2, Upload } from "lucide-react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { useFilePicker } from "./useFilePicker";
import type { FileRejection, FileValidator } from "./types";

export interface FileUploadButtonProps {
  accept?: string;
  multiple?: boolean;
  validate?: FileValidator;
  onFilesAdmitted: (files: File[]) => void;
  onRejected?: (rejections: FileRejection[]) => void;
  /** External busy state (e.g. an upload mutation's isPending) layered on
   *  top of the picker's own isValidating, so the button shows a single
   *  unified spinner regardless of which phase is in flight. */
  isBusy?: boolean;
  disabled?: boolean;
  idleLabel: string;
  busyLabel?: string;
  variant?: ButtonProps["variant"];
  className?: string;
  icon?: ReactNode;
}

export function FileUploadButton({
  accept,
  multiple,
  validate,
  onFilesAdmitted,
  onRejected,
  isBusy,
  disabled,
  idleLabel,
  busyLabel = "Uploading…",
  variant,
  className,
  icon,
}: FileUploadButtonProps) {
  const picker = useFilePicker({ accept, multiple, validate, onFilesAdmitted, onRejected, disabled: disabled || isBusy });
  const busy = isBusy || picker.isValidating;

  return (
    <>
      <input {...picker.inputProps} />
      <Button type="button" variant={variant} disabled={disabled || busy} onClick={picker.openFilePicker} className={className}>
        {busy ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {busyLabel}
          </>
        ) : (
          <>
            {icon ?? <Upload className="w-4 h-4 mr-2" />} {idleLabel}
          </>
        )}
      </Button>
    </>
  );
}
